'use client'

import { useMemo } from 'react'
import { useGoogleApiLoader } from '@hooks/use-google-api-loader'

export type PlacePrediction = google.maps.places.PlacePrediction

export const scmgAutocompleteFilter: Pick<
  google.maps.places.AutocompleteRequest,
  'locationRestriction' | 'includedPrimaryTypes' | 'includedRegionCodes' | 'language' | 'region'
> = {
  // Keep Sharp's Google Places flow unchanged and express SCMG-specific behavior
  // only through this shared autosuggest rectangle. It does not determine which
  // SCMG providers appear in search results, and should stay synchronized with
  // fallback validation instead of adding custom filtering logic.
  locationRestriction: {
    north: 33.95,
    south: 32.534,
    east: -115,
    west: -118.01,
  },
  includedPrimaryTypes: ['neighborhood', 'locality', 'postal_code'],
  includedRegionCodes: ['US'],
  language: 'en-US',
  region: 'US',
}

export const predictionsCache = new Map<string, PlacePrediction[]>()

/**
 * Fetch and cache Places autocomplete predictions for a user-entered query using SCMG's configured bounds.
 */
const getPredictions = async (
  autocompleteSuggestion: typeof google.maps.places.AutocompleteSuggestion,
  input: string,
) => {
  if (predictionsCache.has(input)) {
    return predictionsCache.get(input) || []
  }

  const predictions = await autocompleteSuggestion.fetchAutocompleteSuggestions({
    input,
    ...scmgAutocompleteFilter,
  })

  if (predictions.suggestions.length) {
    const result = predictions.suggestions.map((suggestion) => suggestion.placePrediction).filter((prediction) => prediction !== null)
    predictionsCache.set(input, result)
    return result
  }

  return []
}

/**
 * Expose the loaded Google Places autocomplete constructor and a prediction fetcher once the Maps SDK is ready.
 */
export function useAutocompleteSuggestion() {
  const { isLoaded, loadError } = useGoogleApiLoader()

  return useMemo(() => {
    if (isLoaded) {
      const autocompleteSuggestion = google.maps.places.AutocompleteSuggestion
      return {
        autocompleteSuggestion,
        getPredictions: (input: string) => getPredictions(autocompleteSuggestion, input),
        isLoaded,
        loadError,
      }
    }

    return {
      autocompleteSuggestion: null,
      getPredictions: null,
      isLoaded,
      loadError,
    }
  }, [isLoaded, loadError])
}